/**
 * Driver o'ng tugma menyusi: elementlar (features/drivers/menu.ts) + kerakli oynalar.
 * Sahifa `actions.dialogs` ni bir marta chizadi va har qator/kartani <ContextMenu items={() => actions.items(d)}> bilan o'raydi.
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { absoluteUrl, callPhone, copyText, openInNewTab } from "@/lib/clipboard";
import { useMe } from "@/features/auth/hooks";
import { useArchiveDriver } from "@/features/drivers/api";
import { driverMenu } from "@/features/drivers/menu";
import { driverTz } from "@/features/drivers/timezones";
import type { DriverListItem, DriverStatus } from "@/features/drivers/types";
import type { MenuEntry } from "@/components/ui/context-menu";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { AddReminderModal } from "./AddReminderModal";
import { ChangeStatusModal } from "./ChangeStatusModal";
import { QuickActivityModal } from "./QuickActivityModal";
import { TransferDriverModal } from "./TransferDriverModal";
import { usePersonNav } from "@/features/people/usePersonNav";

type Dialog =
  | { kind: "status"; driver: DriverListItem; status: DriverStatus }
  | { kind: "activity"; driver: DriverListItem; activity: "call" | "note" }
  | { kind: "reminder"; driver: DriverListItem }
  | { kind: "transfer"; driver: DriverListItem };

export interface DriverActionsOptions {
  /** Pipeline: status o'zgarishi sudrash bilan bir xil yo'ldan (ish/sabab oynalari, optimistik ko'chirish) */
  mode?: "list" | "pipeline" | "profile";
  onMove?: (driver: DriverListItem, to: DriverStatus) => void;
  onMarkLost?: (driver: DriverListItem) => void;
}

export function useDriverActions({ mode = "list", onMove, onMarkLost }: DriverActionsOptions = {}) {
  const me = useMe().data;
  const navigate = useNavigate();
  const { t } = useTranslation("menu");
  const { t: tc } = useTranslation();
  // mutateAsync barqaror (useMutation natijasi esa har render'da yangi obyekt): menyu funksiyasi ham barqaror qoladi
  const archiveAsync = useArchiveDriver().mutateAsync;
  const personNav = usePersonNav();
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const archiveDriver = useCallback(
    async (d: DriverListItem) => {
      const ok = await confirmDialog({ title: t("driver.archiveTitle", { name: d.full_name }), description: t("driver.archiveDesc"), confirmLabel: t("archive"), danger: true });
      if (!ok) return;
      try {
        await archiveAsync(d.id);
        toast.success(t("driver.archived", { name: d.full_name }));
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : t("failed"));
      }
    },
    [archiveAsync, t],
  );

  const items = useCallback(
    (d: DriverListItem): MenuEntry[] => {
      if (!me) return [];
      const path = `/drivers/${d.id}`;
      return driverMenu(
        d,
        me,
        {
          open: () => navigate(path),
          openNewTab: () => openInNewTab(path),
          copyLink: () => void copyText(absoluteUrl(path)),
          copy: (text) => void copyText(text),
          call: callPhone,
          navigate: (p) => navigate(p),
          changeStatus: (to) => (onMove ? onMove(d, to) : setDialog({ kind: "status", driver: d, status: to })),
          markLost: onMarkLost ? () => onMarkLost(d) : undefined,
          logActivity: (activity) => setDialog({ kind: "activity", driver: d, activity }),
          addReminder: () => setDialog({ kind: "reminder", driver: d }),
          transfer: () => setDialog({ kind: "transfer", driver: d }),
          archive: () => void archiveDriver(d),
          profile: personNav,
        },
        t,
        tc,
        mode,
      );
    },
    [me, navigate, onMove, onMarkLost, archiveDriver, personNav, t, tc, mode],
  );

  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const dialogs = (
    <>
      {dialog?.kind === "status" && <ChangeStatusModal driver={dialog.driver} open onOpenChange={close} initialStatus={dialog.status} />}
      {dialog?.kind === "activity" && <QuickActivityModal driver={dialog.driver} type={dialog.activity} open onOpenChange={close} />}
      {dialog?.kind === "reminder" && (
        <AddReminderModal driverId={dialog.driver.id} dTz={driverTz(dialog.driver.state)} open onOpenChange={close} defaultAssignee={dialog.driver.owner?.id ?? null} />
      )}
      {dialog?.kind === "transfer" && <TransferDriverModal driver={dialog.driver} open onOpenChange={close} />}
    </>
  );

  return { items, dialogs };
}
